export type LegacyRouteResolution =
  | {
      kind: 'internal';
      path: string;
      supported: boolean;
      reason?: string;
    }
  | {
      kind: 'external';
      url: string;
      supported: true;
    }
  | {
      kind: 'popup';
      appId: string;
      subPath?: string;
      supported: true;
    };

import { getRouterInstance } from './router-instance';
import { isPopupMfe, closesPopupOnNavigation } from '@/mfe/popup-mfe-constants';
import { openPopupMfe, closePopupMfe, usePopupMfeStore } from '@/mfe/popup-store';

interface LegacyPopupOutlet {
  primaryPath: string;
  popupAppId: string;
}

function normalizeTarget(target: string): string {
  return target.trim().replace(/^\/+/, '').replace(/\/+$/, '');
}

function isExternal(target: string): boolean {
  return /^https?:\/\//i.test(target);
}

function toAppPath(target: string): string {
  const normalized = normalizeTarget(target);
  if (!normalized) return '/';
  if (normalized.startsWith('app/')) return `/${normalized}`;

  // Admin apps KEEP their `admin/apps/` prefix so the URL and the MFE base-href
  // match the legacy admin route `/{clientId}/app-container/admin/apps/{appId}`
  // (raptor `app-container-routing.module.ts`, pattern `admin/apps/:app-id`).
  // Stripping it dropped the admin context from the URL (bookmarks) and from
  // role validation. `adminAppRedirectRoute` forwards this `/app/admin/apps/...`
  // form to the client-scoped admin URL.
  if (normalized.startsWith('admin/apps/')) {
    const [appIdRaw, ...tail] = normalized.slice('admin/apps/'.length).split('/').filter(Boolean);
    if (!appIdRaw) return '/';
    const appId = appIdRaw.toLowerCase();
    return `/app/admin/apps/${appId}${tail.length ? `/${tail.join('/')}` : ''}`;
  }

  // Workspace apps KEEP their `workspace/apps/` prefix so the URL and the MFE
  // base-href match the legacy route `/{clientId}/app-container/workspace/apps/
  // {appId}` (raptor `app-container-routing.module.ts`, pattern
  // `workspace/apps/:app-id`; base-href is the FULL path per
  // `mfe-container.component.ts:262`). Stripping it mounted the app at
  // `/{clientId}/app-container/{appId}` with a DIFFERENT base-href than the
  // deployed workspace MFE expects and broke legacy bookmarks
  // (`.../workspace/apps/usir` matched the generic route as appId=`workspace`).
  // `workspaceAppRedirectRoute` forwards this `/app/workspace/apps/...` form to
  // the client-scoped workspace URL — mirroring the admin handling above.
  if (normalized.startsWith('workspace/apps/')) {
    const [appIdRaw, ...tail] = normalized
      .slice('workspace/apps/'.length)
      .split('/')
      .filter(Boolean);
    if (!appIdRaw) return '/';
    const appId = appIdRaw.toLowerCase();
    return `/app/workspace/apps/${appId}${tail.length ? `/${tail.join('/')}` : ''}`;
  }

  // Strip the remaining legacy app-container route prefix so the REAL appId is
  // the first remaining segment. Mirrors `app-container-routing.module.ts`
  // pattern `:app-id`.
  let rest = normalized;
  for (const prefix of ['apps/']) {
    if (rest.startsWith(prefix)) {
      rest = rest.slice(prefix.length);
      break;
    }
  }
  const [appIdRaw, ...tail] = rest.split('/').filter(Boolean);
  if (!appIdRaw) return '/';
  const appId = appIdRaw.toLowerCase();
  return `/app/${appId}${tail.length ? `/${tail.join('/')}` : ''}`;
}

export function parseLegacyPopupOutletPath(pathname: string): LegacyPopupOutlet | null {
  const match = /^(?<primary>.*)\(popup:apps\/(?<popup>[a-z0-9_-]+)(?:\/[^)]*)?\)$/i.exec(
    pathname.trim(),
  );
  if (!match?.groups) return null;

  const primaryPath = match.groups.primary.replace(/\/+$/, '') || '/';
  const popupAppId = match.groups.popup.toLowerCase();
  if (!isPopupMfe(popupAppId)) return null;

  return { primaryPath, popupAppId };
}

export function resolveLegacyRouteTarget(target: string): LegacyRouteResolution {
  const normalized = normalizeTarget(target);
  if (isExternal(normalized)) {
    return { kind: 'external', url: normalized, supported: true };
  }

  // Popup MFE targets (`apps/stf`, `apps/mcpchatbot`, `apps/stf/contact-support`, …)
  // mount in the popup overlay (the legacy named `popup` outlet), not the routed page.
  const popupMatch = /^apps\/([a-z0-9_-]+)(?:\/(.+))?$/i.exec(normalized);
  if (popupMatch && isPopupMfe(popupMatch[1])) {
    return {
      kind: 'popup',
      appId: popupMatch[1].toLowerCase(),
      subPath: popupMatch[2] || undefined,
      supported: true,
    };
  }

  return { kind: 'internal', path: toAppPath(normalized), supported: true };
}

export function navigateBrowserToLegacyTarget(target: string): LegacyRouteResolution {
  const resolution = resolveLegacyRouteTarget(target);
  if (!resolution.supported) return resolution;

  if (resolution.kind === 'popup') {
    openPopupMfe(resolution.appId, resolution.subPath);
    return resolution;
  }

  if (resolution.kind === 'external') {
    window.open(resolution.url, '_blank', 'noopener,noreferrer');
    return resolution;
  }

  // Drive navigation through the router's OWN `navigate()` — the exact call the
  // working side-nav rail uses (`router.navigate({ to })`). A raw
  // `window.history.pushState` + synthetic `popstate` changes the URL bar but
  // TanStack Router does not react to it, so the matched route never
  // re-rendered ("URL changes but the MFE doesn't open"). Fall back to the
  // History API only if the router hasn't registered yet (pre-bootstrap).
  //
  // A popup overlay open at navigation time is handled per its parity policy
  // (`closesPopupOnNavigation`): the Orchestrator window closes when it launches
  // another MFE (Angular dropped the named `popup` outlet on primary navigation),
  // so we clear the store and navigate WITHOUT its `?popup=` param — carrying the
  // param forward would let the Store→URL sync re-open the overlay. Every other
  // popup keeps its `?popup=<appId>` param so an MFE-initiated context navigation
  // (e.g. mcpchatbot on load) doesn't strip it and trip the URL→Store sync into
  // closing the overlay. Angular is immune because its popup lives in a named
  // router outlet unaffected by search params.
  const router = getRouterInstance();
  if (router) {
    const popupAppId = usePopupMfeStore.getState().appId;
    if (popupAppId && closesPopupOnNavigation(popupAppId)) {
      closePopupMfe();
      void router.navigate({ to: resolution.path });
    } else {
      const popupSubPath = usePopupMfeStore.getState().subPath;
      const search = popupAppId
        ? `?popup=${encodeURIComponent(popupAppId)}${popupSubPath ? `&popupRoute=${encodeURIComponent(popupSubPath)}` : ''}`
        : '';
      void router.navigate({ href: `${resolution.path}${search}` });
    }
  } else {
    window.history.pushState(null, '', resolution.path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
  return resolution;
}
